/* import __COLOCATED_TEMPLATE__ from './suggestion-summary-actions.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { EntityType } from 'embercom/models/data/entity-types';
import type { ReviewableContent } from 'embercom/services/suggestion-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    isEditReviewType: boolean;
    allEditsHaveBeenActioned: boolean;
    reviewEditsButtonLabel: string;
    onGoToFirstSuggestion: () => void;
    reviewRequest: ContentReviewRequest;
    handleSuggestion: (
      reviewRequest: any,
      approved: boolean,
      entityTypeToApproveAs?: EntityType,
    ) => void;
    isSaving: boolean;
    isRejecting: boolean;
    isApproving: boolean;
    activeContent: ReviewableContent;
    canMoveContent: boolean;
    isCondupeSuggestion?: boolean;
    handleCondupeSuggestion?: (action: 'accept' | 'reject') => void;
  };
}

export default class SuggestionSummaryActions extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;

  get canUseAddContentDropdown() {
    return (
      !this.appService.app.isStandaloneApp && !this.args.reviewRequest?.isUnderperformingContent
    );
  }

  get canUseDoneButton() {
    return this.args.reviewRequest?.isUnderperformingContent;
  }

  get isAddContentButtonDisabled() {
    return this.args.isSaving || this.args.isApproving;
  }

  private trackAddButtonAnalytics(approvedAsEntityType: EntityType, objectName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: objectName,
      entity_id: this.args.reviewRequest.entityId,
      entity_type: this.args.reviewRequest.entityType,
      approved_as_entity_type: approvedAsEntityType,
    });
  }

  get addContentButtonGroupList() {
    let options = [
      {
        items: [
          {
            text: this.intl.t(
              'knowledge-hub.content-editor.review.suggestion-summary-card.add-snippet',
            ),
            icon: 'new',
            isDisabled: this.isAddContentButtonDisabled,
            onSelectItem: () => {
              this.trackAddButtonAnalytics(
                EntityType.ContentSnippet,
                'suggestion_summary_card_new_snippet_option',
              );
              this.args.handleSuggestion(this.args.reviewRequest, true, EntityType.ContentSnippet); // Performs approve as snippet
            },
          },
          {
            text: this.intl.t(
              'knowledge-hub.content-editor.review.suggestion-summary-card.add-article',
            ),
            icon: 'new',
            isDisabled: this.isAddContentButtonDisabled,
            onSelectItem: () => {
              this.trackAddButtonAnalytics(
                EntityType.ArticleContent,
                'suggestion_summary_card_new_article_option',
              );
              this.args.handleSuggestion(this.args.reviewRequest, true, EntityType.ArticleContent); // Performs approve as article
            },
          },
        ],
      },
      ...(this.args.canMoveContent
        ? [
            {
              items: [
                {
                  text: this.intl.t(
                    'knowledge-hub.content-editor.review.suggestion-summary-card.add-to-existing-content',
                  ),
                  icon: 'article',
                  isDisabled: this.isAddContentButtonDisabled,
                  component:
                    'knowledge-hub/content-editor/content-review/add-to-existing-content-item',
                  reviewRequest: this.args.reviewRequest,
                  activeContent: this.args.activeContent,
                  componentShouldReplaceItem: true,
                  onSelectItem: () => {},
                },
              ],
            },
          ]
        : []),
    ];

    return options;
  }

  @action
  handleCondupeSuggestion(action: 'accept' | 'reject') {
    if (!action) {
      console.error('handleCondupeSuggestion: action parameter is required but was not provided');
      return;
    }

    if (action !== 'accept' && action !== 'reject') {
      console.error(
        `handleCondupeSuggestion: invalid action "${action}". Expected "accept" or "reject"`,
      );
      return;
    }

    try {
      this.args.handleCondupeSuggestion?.(action);
    } catch (error) {
      console.error('handleCondupeSuggestion: error occurred while executing callback:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::SuggestionSummaryActions': typeof SuggestionSummaryActions;
    'knowledge-hub/content-editor/content-review/suggestion-summary-actions': typeof SuggestionSummaryActions;
  }
}
